import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, BarChart3, Users, Sparkles } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import TestimonialsSection from "@/components/site/TestimonialsSection";
import SEO, { ORG_SCHEMA } from "@/components/site/SEO";
import { HOME } from "@/constants/testIds";
import { PILLARS } from "@/data/pillars";
import { usePageContent } from "@/lib/usePageContent";

const Home = () => {
    const { get } = usePageContent("home");
    return (
        <div data-testid="home-page">
            <SEO
                title="The HUMAN+ Framework for the AI era"
                description="Research-informed consulting that helps organizations adopt AI while strengthening the human capabilities technology cannot replace. Take the free HUMAN+ Snapshot™."
                path="/"
                schema={ORG_SCHEMA}
            />
            {/* HERO */}
            <section className="relative overflow-hidden bg-white">
                <div className="ih-container pt-16 pb-20 md:pt-24 md:pb-28 grid gap-12 lg:gap-16 lg:grid-cols-[1.05fr_1fr] lg:items-center">
                    <div className="animate-fade-up">
                        <p className="ih-eyebrow">
                            {get("hero.eyebrow", "Research-Informed · Executive-Ready")}
                        </p>
                        <h1
                            data-testid={HOME.heroTitle}
                            className="mt-4 font-montserrat font-bold text-indigo_brand text-[2.4rem] sm:text-5xl lg:text-[3.6rem] leading-[1.05] tracking-tight"
                        >
                            {get("hero.title", "Adopt AI without losing what makes your people irreplaceable.")}
                        </h1>
                        <p className="mt-7 font-opensans text-base sm:text-lg leading-relaxed text-[#4A5568] max-w-xl">
                            {get("hero.subtitle", "The Intuitus Hub™ helps organizations adopt AI while strengthening the human capabilities technology cannot replace — through the proprietary HUMAN+ Framework™.")}
                        </p>

                        <div className="mt-9 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/snapshot"
                                data-testid={HOME.heroCta}
                                className="ih-btn-primary"
                            >
                                {get("hero.primary_cta", "Take the FREE HUMAN+ Snapshot™")}
                                <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/framework"
                                data-testid={HOME.heroSecondaryCta}
                                className="ih-btn-secondary"
                            >
                                {get("hero.secondary_cta", "Explore the Framework")}
                            </Link>
                        </div>

                        <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
                            {[
                                { n: "6", l: "HUMAN+ Pillars" },
                                { n: "10", l: "Minute Snapshot" },
                                { n: "1:1", l: "Executive Report" },
                            ].map((s) => (
                                <div key={s.l}>
                                    <p className="font-montserrat font-bold text-indigo_brand text-2xl sm:text-3xl">
                                        {s.n}
                                    </p>
                                    <p className="font-poppins text-[11px] uppercase tracking-[0.14em] text-[#4A5568] mt-1">
                                        {s.l}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Hero visual */}
                    <div className="relative animate-fade-up">
                        <div className="relative rounded-3xl overflow-hidden shadow-medium aspect-[4/5] bg-surface">
                            <img
                                src="https://images.pexels.com/photos/5466285/pexels-photo-5466285.jpeg?auto=compress&cs=tinysrgb&w=1200"
                                alt="Diverse leadership team collaborating in a bright modern office"
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-indigo_brand/55 via-indigo_brand/5 to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6">
                                <p className="ih-quote text-white text-lg leading-snug">
                                    &ldquo;AI changes the work. The human still does the leading.&rdquo;
                                </p>
                            </div>
                        </div>

                        {/* Floating accent card */}
                        <div className="hidden sm:block absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-medium p-5 max-w-[260px]">
                            <div className="flex items-center gap-3">
                                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-green_brand/15">
                                    <Sparkles size={18} className="text-green_brand" />
                                </span>
                                <p className="font-raleway font-semibold text-indigo_brand text-sm">
                                    HUMAN+ Readiness
                                </p>
                            </div>
                            <p className="font-opensans text-xs text-[#4A5568] mt-2 leading-relaxed">
                                Discover where your organization stands across six
                                human-centered capability pillars.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Subtle green accent strip */}
                <div className="h-[3px] w-24 bg-green_brand ml-6 md:ml-10 lg:ml-16" />
            </section>

            {/* INTRO TO THE INTUITUS HUB */}
            <section className="ih-section bg-surface" data-testid={HOME.introHub}>
                <div className="ih-container grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
                    <div className="relative rounded-3xl overflow-hidden shadow-soft aspect-[4/3]">
                        <img
                            src="https://images.pexels.com/photos/7491017/pexels-photo-7491017.jpeg?auto=compress&cs=tinysrgb&w=1100"
                            alt="Diverse professional women in strategy session"
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                    <div>
                        <SectionHeading
                            eyebrow="Who We Are"
                            title="The Intuitus Hub™ — research-informed consulting for the AI era."
                            kicker="We help organizations adopt AI responsibly while strengthening the human capabilities that technology cannot replace: awareness, empathy, adaptability, partnership, transformation leadership, and flourishing."
                        />
                        <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                            {[
                                { icon: Users, label: "Built for executive decision-makers" },
                                { icon: BarChart3, label: "Grounded in published research" },
                                { icon: CheckCircle2, label: "Measurable behaviors and outcomes" },
                                { icon: Sparkles, label: "Premium, human-centered delivery" },
                            ].map(({ icon: Icon, label }) => (
                                <li
                                    key={label}
                                    className="flex items-start gap-3 ih-card p-5"
                                >
                                    <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-indigo_brand/8 shrink-0">
                                        <Icon size={18} className="text-indigo_brand" />
                                    </span>
                                    <span className="font-raleway font-semibold text-indigo_brand text-sm leading-snug">
                                        {label}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8">
                            <Link to="/about" className="ih-btn-ghost">
                                Read our mission
                                <ArrowRight size={15} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* INTRO TO HUMAN+ FRAMEWORK */}
            <section className="ih-section bg-white" data-testid={HOME.introFramework}>
                <div className="ih-container">
                    <SectionHeading
                        eyebrow="The Framework"
                        title="HUMAN+ Framework™"
                        kicker="Six human-centered capability pillars that determine whether AI strengthens your organization — or quietly hollows it out. Every consulting engagement, workshop, and assessment is built on this framework."
                    />

                    <div
                        data-testid={HOME.pillarsGrid}
                        className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                    >
                        {PILLARS.map((p, i) => (
                            <div
                                key={p.slug}
                                className="ih-card p-7 group relative overflow-hidden"
                            >
                                <span
                                    className="absolute top-0 left-0 h-[3px] w-12 bg-green_brand"
                                    aria-hidden="true"
                                />
                                <p className="font-poppins font-medium text-green_brand text-xs tracking-[0.2em]">
                                    PILLAR {p.number}
                                </p>
                                <h3 className="mt-3 font-montserrat font-bold text-indigo_brand text-xl leading-snug">
                                    {p.name}
                                </h3>
                                <p className="mt-3 font-opensans text-sm text-[#4A5568] leading-relaxed">
                                    {p.short}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:items-center">
                        <Link to="/framework" className="ih-btn-secondary">
                            Read the full framework
                        </Link>
                        <Link to="/snapshot" className="ih-btn-ghost ml-1">
                            or take the Snapshot
                            <ArrowRight size={15} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* BOTTOM CTA */}
            <section
                className="ih-section bg-surface"
                data-testid={HOME.bottomCta}
            >
                <div className="ih-container">
                    <div className="ih-card p-10 md:p-14 grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center shadow-medium">
                        <div>
                            <p className="ih-eyebrow">Free · 10 Minutes · Personalized</p>
                            <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl leading-tight">
                                Get your organization&rsquo;s HUMAN+ Readiness score
                                <span className="block text-gold_brand">today.</span>
                            </h2>
                            <p className="mt-5 font-opensans text-[#4A5568] text-base max-w-xl leading-relaxed">
                                Take the HUMAN+ Snapshot™ and receive a personalized
                                Executive Report — your six-pillar scorecard, strengths,
                                growth opportunities, and a clear recommendation.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-end">
                            <Link to="/snapshot" className="ih-btn-primary">
                                Take the FREE Snapshot
                                <ArrowRight size={16} />
                            </Link>
                            <Link to="/discovery" className="ih-btn-secondary">
                                Schedule a Discovery Call
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
            <TestimonialsSection page="home" />
        </div>
    );
};

export default Home;
